// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    components,
    GroupBase,
    GroupProps,
    MenuProps,
    PropsValue,
} from 'react-select';
import { ISelectRequiredOption } from 'common/components';
import {
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import { reduceGroupedOptions } from 'react-select-async-paginate';
import AsyncSelectInput from 'common/components/Select/AsyncSelectInput';
import {
    IAdditionalOptions,
    ILoadOptions,
} from 'common/components/SelectInput';
import { FixedFormField } from 'admin/common/components/Filters/Styles';
import { FILTERS } from 'admin/common/constants/tests';
import { COMPUTE_RESOURCE_HA_STATUS } from 'admin/computeResource/constants';
import { failoverDomains } from 'common/api/resources/FailoverDomain';
import { COLORS } from 'common/theme';

export const HA_FILTER_GROUP = 'ha_status';
export const FD_FILTER_GROUP = 'fd';

export interface IHaFilterOption extends ISelectRequiredOption {
    group: typeof HA_FILTER_GROUP | typeof FD_FILTER_GROUP;
}

interface IHighAvailabilityFilterProps {
    onChange: (option: PropsValue<IHaFilterOption>) => void;
    value?: IHaFilterOption;
    // Failover domains come from an endpoint that requires HA permissions; without them only statuses are offered.
    withFailoverDomains?: boolean;
}

export const HA_STATUS_TRANSLATION_MAP: Record<COMPUTE_RESOURCE_HA_STATUS, string> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: 'computeResource.servers.haStatuses.protected',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: 'computeResource.servers.haStatuses.notProtected',
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: 'computeResource.servers.haStatuses.atRisk',
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: 'computeResource.servers.haStatuses.unavailable',
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: 'computeResource.servers.haStatuses.eligible',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: 'computeResource.servers.haStatuses.notEligible',
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: 'computeResource.servers.haStatuses.paused',
};

const toGroups = (
    label: typeof HA_FILTER_GROUP | typeof FD_FILTER_GROUP,
    options: IHaFilterOption[]
): Array<GroupBase<IHaFilterOption>> => options.length > 0 ? [{ label, options }] : [];

/**
 * Options loader for the HA filter: the static HA statuses (filtered by the typed text)
 * are added only while nothing is loaded yet (`prevOptions` is empty), failover domains are
 * fetched page by page with backend search. Keying on `prevOptions` rather than the page number
 * keeps a retried first page from adding the statuses a second time.
 * Without `withFailoverDomains` (the user may not read failover domains, so the endpoint answers 403)
 * only the statuses are offered and the API is never called.
 * Group labels must differ so `reduceGroupedOptions` appends each page to the domain group.
 */
export const createHaFilterOptionsLoader = (
    haOptions: IHaFilterOption[],
    withFailoverDomains = true
): ILoadOptions<IHaFilterOption> => async (search, prevOptions, additional) => {
    const page = additional?.page ?? 1;
    const needle = search.trim().toLowerCase();
    const haGroups = prevOptions.length === 0
        ? toGroups(HA_FILTER_GROUP, haOptions.filter(option => option.label.toLowerCase().includes(needle)))
        : [];

    if (!withFailoverDomains) {
        return { options: haGroups, hasMore: false };
    }

    try {
        const { data: { data, links } } = await failoverDomains.list({
            page,
            filters: { search },
        });

        return {
            options: [
                ...haGroups,
                ...toGroups(FD_FILTER_GROUP, data.map(item => ({
                    value: item.id.toString(),
                    label: item.name,
                    group: FD_FILTER_GROUP,
                }))),
            ],
            hasMore: !!links.next,
            additional: { page: page + 1 },
        };
    } catch (e) {
        // The first failure of the first page still shows the statuses and lets the library retry the
        // same page (no `additional`, so the page number stays). A repeated failure throws, so the
        // library applies `reloadOnErrorTimeout` instead of retrying every 300 ms and spamming toasts.
        if (haGroups.length > 0) {
            return { options: haGroups, hasMore: true };
        }

        throw e;
    }
};

const GroupWithSeparator = (
    props: GroupProps<IHaFilterOption, false, GroupBase<IHaFilterOption>>
) => {
    const isFirstGroup = props.selectProps.options?.[0] === props.data;
    return (
        <div style={isFirstGroup ? undefined : {
            borderTop: `1px solid ${COLORS.GREY_40}`,
            marginTop: 4,
            paddingTop: 4,
        }}>
            <components.Group {...props} />
        </div>
    );
};

const WideMenu = (
    props: MenuProps<IHaFilterOption, false, GroupBase<IHaFilterOption>>
) => (
    <components.Menu {...props}>
        <div style={{ minWidth: 220 }}>
            {props.children}
        </div>
    </components.Menu>
);

const SELECT_COMPONENTS = {
    Group: GroupWithSeparator,
    GroupHeading: () => null,
    Menu: WideMenu,
};

export const HighAvailabilityFilter: React.FC<IHighAvailabilityFilterProps> = ({
    onChange,
    value,
    withFailoverDomains = true,
}) => {
    const translate = useTranslate();

    const haOptions: IHaFilterOption[] = React.useMemo(() => Object.values(COMPUTE_RESOURCE_HA_STATUS).map(status => ({
        value: status,
        label: translate(HA_STATUS_TRANSLATION_MAP[status]) as string,
        group: HA_FILTER_GROUP,
    })), [translate]);

    // The library keeps loaded options per search text and drops them only when this key changes.
    const haLabelsKey = haOptions.map(option => option.label).join('|');

    const loadOptions = React.useMemo(
        () => createHaFilterOptionsLoader(haOptions, withFailoverDomains),
        [haOptions, withFailoverDomains]
    );

    return (
        <FixedFormField data-cy={FILTERS.HIGH_AVAILABILITY}>
            <AsyncSelectInput<IHaFilterOption, IAdditionalOptions, GroupBase<IHaFilterOption>>
                inputId="high-availability-filter"
                placeholder={<Translate content="filters.highAvailability" />}
                loadOptions={loadOptions}
                cacheUniqs={[haLabelsKey, withFailoverDomains]}
                additional={{ page: 1 }}
                reduceOptions={reduceGroupedOptions}
                debounceTimeout={withFailoverDomains ? 1000 : 0}
                reloadOnErrorTimeout={5000}
                isClearable={true}
                onChange={onChange}
                value={value}
                menuPosition="absolute"
                menuPortalTarget={document.body}
                maxMenuHeight={400}
                components={SELECT_COMPONENTS}
            />
        </FixedFormField>
    );
};
